import React, { useState, useEffect, useCallback } from "react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

// Action values grouped for the audit-log filter dropdown. Must stay in
// sync with AuditAction in backend/app/models/audit_log.py — adding a new
// enum value there means adding it here for it to be filterable from the
// UI. The free-text "custom" fallback lets operators filter on values we
// forgot to add here without redeploying.
const AUDIT_ACTION_GROUPS = [
  { label: "Auth", actions: ["login", "logout", "password_change"] },
  { label: "Campaign", actions: ["campaign_created", "campaign_status_change", "campaign_updated", "campaign_auto_paused"] },
  { label: "Message", actions: ["message_sent", "message_failed", "message_scheduled", "message_sent_from_queue", "message_canceled"] },
  { label: "Compliance", actions: ["quiet_hours_blocked"] },
  { label: "Budget", actions: ["budget_alert_80", "budget_alert_95", "budget_cap_reached"] },
  { label: "Contacts", actions: ["contacts_uploaded", "contact_skipped", "contact_opt_out"] },
  { label: "User admin", actions: ["user_created", "user_updated", "user_deactivated"] },
  { label: "Org", actions: ["org_settings_updated"] },
  { label: "Numbers", actions: ["number_provisioned", "number_released", "pool_created"] },
  { label: "Export", actions: ["export_generated"] },
  { label: "Reconciliation", actions: ["cost_reconciliation_flagged"] },
];

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [org, setOrg] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "texter" });
  const [activeTab, setActiveTab] = useState("users");
  const [loading, setLoading] = useState(true);

  // Twilio auth token is write-only: the API never returns it, so we track
  // the "new value the admin typed" separately from the `org` object.
  // Empty string = don't change on save.
  const [twilioAuthTokenInput, setTwilioAuthTokenInput] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [orgSaveStatus, setOrgSaveStatus] = useState(null);  // "saving" | "saved" | "error"

  // Audit log state. Filters are applied on change; pagination via page/per_page.
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPages, setAuditPages] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPerPage] = useState(50);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditFilters, setAuditFilters] = useState({
    action: "",
    user_id: "",
    start_date: "",
    end_date: "",
  });

  // Rate-limits tab (Phase 28). Read-only — the operator sees what's
  // configured but can't change it from here. Lives behind the same
  // admin_required guard as the rest of the page.
  const [rateLimits, setRateLimits] = useState(null);
  const [rateLimitsLoading, setRateLimitsLoading] = useState(false);
  const [rateLimitsError, setRateLimitsError] = useState(null);

  const fetchRateLimits = useCallback(async () => {
    setRateLimitsLoading(true);
    setRateLimitsError(null);
    try {
      const res = await api.get("/admin/rate-limits");
      setRateLimits(res.data);
    } catch (err) {
      setRateLimitsError(errorToUserMessage(err, "Failed to load rate limits."));
      setRateLimits(null);
    } finally {
      setRateLimitsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "rate-limits") {
      fetchRateLimits();
    }
  }, [activeTab, fetchRateLimits]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/users").catch(() => ({ data: { users: [] } })),
      api.get("/admin/org").catch(() => ({ data: {} })),
    ]).then(([usersRes, orgRes]) => {
      setUsers(usersRes.data.users || []);
      setOrg(orgRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    setAuditError(null);
    const params = { page: auditPage, per_page: auditPerPage };
    if (auditFilters.action) params.action = auditFilters.action;
    if (auditFilters.user_id) params.user_id = auditFilters.user_id;
    if (auditFilters.start_date) params.start_date = auditFilters.start_date;
    if (auditFilters.end_date) params.end_date = auditFilters.end_date;
    try {
      const res = await api.get("/admin/audit-logs", { params });
      setAuditLogs(res.data.audit_logs || []);
      setAuditTotal(res.data.total || 0);
      setAuditPages(res.data.pages || 0);
    } catch (err) {
      setAuditError(errorToUserMessage(err, "Failed to load audit log."));
      setAuditLogs([]);
      setAuditTotal(0);
      setAuditPages(0);
    } finally {
      setAuditLoading(false);
    }
  }, [auditPage, auditPerPage, auditFilters]);

  useEffect(() => {
    if (activeTab === "audit") {
      fetchAuditLogs();
    }
  }, [activeTab, fetchAuditLogs]);

  const updateAuditFilter = (key, value) => {
    setAuditFilters((f) => ({ ...f, [key]: value }));
    setAuditPage(1);  // any filter change resets to page 1
  };

  const resetAuditFilters = () => {
    setAuditFilters({ action: "", user_id: "", start_date: "", end_date: "" });
    setAuditPage(1);
  };

  // Used by the audit table to turn a user_id into a display name.
  const userNameById = (id) => {
    if (id === null || id === undefined) return "system";
    const u = users.find((u) => u.id === id);
    return u ? u.name : `user #${id}`;
  };

  // Summarize the metadata JSON blob in a single line for the table cell.
  // The full metadata is still shown on hover via the title attribute.
  const summarizeMetadata = (meta) => {
    if (!meta || typeof meta !== "object") return "";
    try {
      const s = JSON.stringify(meta);
      return s.length > 80 ? s.slice(0, 77) + "…" : s;
    } catch (_) {
      return "";
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch (_) {
      return iso;
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/users", newUser);
      setUsers((prev) => [...prev, res.data]);
      setNewUser({ email: "", name: "", password: "", role: "texter" });
      setShowAddUser(false);
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to create user"));
    }
  };

  const toggleUserActive = async (userId, currentActive) => {
    try {
      await api.put(`/admin/users/${userId}`, { is_active: !currentActive });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, is_active: !currentActive } : u))
      );
    } catch (err) {
      console.error("Failed to update user:", err);
    }
  };

  const saveOrgSettings = async () => {
    // Build the payload from the local org state. Only include the auth
    // token if the admin actually typed a new value — otherwise the
    // backend interprets empty/whitespace as "no change".
    const payload = {
      name: org.name,
      twilio_account_sid: org.twilio_account_sid || null,
      default_messaging_service_sid: org.default_messaging_service_sid || null,
      opt_out_keywords: org.opt_out_keywords || [],
      default_export_format: org.default_export_format || "csv",
      sms_cost_per_segment: org.sms_cost_per_segment,
      mms_cost_per_message: org.mms_cost_per_message,
      budget_cap_monthly: org.budget_cap_monthly ?? null,
    };
    if (twilioAuthTokenInput.trim()) {
      payload.twilio_auth_token = twilioAuthTokenInput;
    }

    setOrgSaveStatus("saving");
    try {
      const res = await api.put("/admin/org", payload);
      setOrg(res.data);
      setTwilioAuthTokenInput("");  // Clear the input so it shows the placeholder again.
      setOrgSaveStatus("saved");
      setTimeout(() => setOrgSaveStatus(null), 2500);
    } catch (err) {
      setOrgSaveStatus("error");
      alert(errorToUserMessage(err, "Failed to save settings"));
    }
  };

  const addKeyword = () => {
    const kw = keywordInput.trim().toUpperCase();
    if (!kw) return;
    const current = org.opt_out_keywords || [];
    if (current.includes(kw)) {
      setKeywordInput("");
      return;
    }
    setOrg({ ...org, opt_out_keywords: [...current, kw] });
    setKeywordInput("");
  };

  const removeKeyword = (kw) => {
    const current = org.opt_out_keywords || [];
    setOrg({ ...org, opt_out_keywords: current.filter((k) => k !== kw) });
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Administration</h1>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { id: "users", label: "Users" },
          { id: "organization", label: "Organization" },
          { id: "audit", label: "Audit Log" },
          { id: "rate-limits", label: "Rate Limits" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : activeTab === "audit" ? (
        /* ── Audit Log ──────────────────────────────────────────── */
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  value={auditFilters.action}
                  onChange={(e) => updateAuditFilter("action", e.target.value)}
                >
                  <option value="">All actions</option>
                  {AUDIT_ACTION_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.actions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Actor</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  value={auditFilters.user_id}
                  onChange={(e) => updateAuditFilter("user_id", e.target.value)}
                >
                  <option value="">All users</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  value={auditFilters.start_date}
                  onChange={(e) => updateAuditFilter("start_date", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  value={auditFilters.end_date}
                  onChange={(e) => updateAuditFilter("end_date", e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {auditLoading
                  ? "Loading…"
                  : `${auditTotal} ${auditTotal === 1 ? "entry" : "entries"}`}
                {auditPages > 1 && !auditLoading && (
                  <> · page {auditPage} of {auditPages}</>
                )}
              </span>
              <button
                onClick={resetAuditFilters}
                className="text-xs text-gray-500 hover:text-gray-800 underline"
                disabled={!auditFilters.action && !auditFilters.user_id && !auditFilters.start_date && !auditFilters.end_date}
              >
                Reset filters
              </button>
            </div>
            {auditError && (
              <div className="text-sm text-red-600">{auditError}</div>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">When</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Actor</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Entity</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {auditLogs.length === 0 && !auditLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                      No audit entries match the current filters.
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-600 font-mono whitespace-nowrap">
                        {formatTime(row.created_at)}
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 font-mono">
                        {row.action}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {userNameById(row.user_id)}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600 font-mono">
                        {row.entity_type ? `${row.entity_type}` : "—"}
                        {row.entity_id ? ` #${row.entity_id}` : ""}
                      </td>
                      <td
                        className="px-4 py-2 text-xs text-gray-600 font-mono max-w-md truncate"
                        title={row.metadata ? JSON.stringify(row.metadata, null, 2) : ""}
                      >
                        {summarizeMetadata(row.metadata)}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500 font-mono">
                        {row.ip_address || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {auditPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <button
                onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                disabled={auditPage <= 1 || auditLoading}
                className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <span className="text-xs text-gray-500">
                Page {auditPage} of {auditPages}
              </span>
              <button
                onClick={() => setAuditPage((p) => Math.min(auditPages, p + 1))}
                disabled={auditPage >= auditPages || auditLoading}
                className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      ) : activeTab === "users" ? (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddUser(!showAddUser)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              {showAddUser ? "Cancel" : "+ Add User"}
            </button>
          </div>

          {showAddUser && (
            <form onSubmit={createUser} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="Full name" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
                <input type="email" placeholder="Email" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
                <input type="password" placeholder="Password" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
                <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                  <option value="texter">Texter</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Create User</button>
            </form>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full capitalize">{u.role}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {u.is_active ? "Active" : "Deactivated"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.id !== user.id && (
                        <button
                          onClick={() => toggleUserActive(u.id, u.is_active)}
                          className={`text-xs font-medium ${u.is_active ? "text-red-600 hover:text-red-800" : "text-green-600 hover:text-green-800"}`}
                        >
                          {u.is_active ? "Deactivate" : "Activate"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : activeTab === "rate-limits" ? (
        /* ── Rate Limits ──────────────────────────────────────────
           Static configuration view. Recent 429 events live in the
           Phase 25 structured access logs — operator one-liner is
           shown in the help banner below. */
        <div className="space-y-4">
          {rateLimitsLoading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : rateLimitsError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              {rateLimitsError}
            </div>
          ) : rateLimits ? (
            <>
              {/* Status banner */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between">
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-gray-900">
                    Rate limiter:{" "}
                    <span className={rateLimits.enabled ? "text-green-700" : "text-amber-700"}>
                      {rateLimits.enabled ? "enabled" : "disabled"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Storage: <code className="bg-gray-100 px-1 rounded">{rateLimits.storage}</code>
                    {rateLimits.default_limits.length > 0 && (
                      <>
                        {" · "}Defaults:{" "}
                        {rateLimits.default_limits.map((lim, i) => (
                          <code key={i} className="bg-gray-100 px-1 rounded mr-1">
                            {lim}
                          </code>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Help banner — points at the Phase 25 logs for "who got rate-limited recently". */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                <strong>Recent 429s</strong> aren't shown here — they live in the structured access logs.
                Run on the host:
                <pre className="mt-1 bg-white border border-blue-100 rounded px-2 py-1 overflow-x-auto">
                  {`docker compose logs --since 1h api | jq -c 'select(.status == 429)'`}
                </pre>
              </div>

              {/* Per-endpoint table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Route</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Methods</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Limit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rateLimits.per_endpoint.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-sm text-gray-400">
                          No explicit per-endpoint limits configured. Routes inherit the defaults shown above.
                        </td>
                      </tr>
                    ) : (
                      rateLimits.per_endpoint.map((row) => (
                        <tr key={`${row.endpoint}-${row.rule}`}>
                          <td className="px-4 py-3 text-sm font-mono text-gray-900">{row.rule}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            {row.methods.join(", ")}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 font-mono">{row.endpoint}</td>
                          <td className="px-4 py-3 text-xs text-gray-700">
                            {row.limits.map((lim, i) => (
                              <code key={i} className="bg-gray-100 px-1 rounded mr-1">
                                {lim}
                              </code>
                            ))}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        /* Organization settings */
        <div className="space-y-6">
          {org && (
            <>
              {/* ── General ─────────────────────────────────────── */}
              <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                <h2 className="font-semibold text-gray-900">General</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={org.name || ""}
                    onChange={(e) => setOrg({ ...org, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Budget Cap ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="No cap"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={org.budget_cap_monthly ?? ""}
                    onChange={(e) =>
                      setOrg({
                        ...org,
                        budget_cap_monthly: e.target.value === "" ? null : parseFloat(e.target.value),
                      })
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no cap. Advisory today — per-campaign caps enforce.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Export Format</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={org.default_export_format || "csv"}
                    onChange={(e) => setOrg({ ...org, default_export_format: e.target.value })}
                  >
                    <option value="csv">CSV</option>
                    <option value="xlsx">Excel (XLSX)</option>
                    <option value="van">VAN</option>
                    <option value="pdi">PDI</option>
                  </select>
                </div>
              </section>

              {/* ── Twilio ──────────────────────────────────────── */}
              <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                <h2 className="font-semibold text-gray-900">Twilio</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account SID</label>
                  <input
                    type="text"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    value={org.twilio_account_sid || ""}
                    onChange={(e) => setOrg({ ...org, twilio_account_sid: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auth Token</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder={org.twilio_account_sid ? "(unchanged)" : "Paste token to set"}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    value={twilioAuthTokenInput}
                    onChange={(e) => setTwilioAuthTokenInput(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Write-only. Leave blank to keep the existing token. The server never returns it.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Used to validate inbound Twilio webhook signatures for this org. The
                    deploy-level <code>TWILIO_AUTH_TOKEN</code> env var is a fallback for
                    webhooks from unknown AccountSids — no need to update it when you set a per-org token here.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Messaging Service SID</label>
                  <input
                    type="text"
                    placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    value={org.default_messaging_service_sid || ""}
                    onChange={(e) => setOrg({ ...org, default_messaging_service_sid: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Fallback sender when a campaign's pool has no Messaging Service.</p>
                </div>
              </section>

              {/* ── Opt-out keywords ────────────────────────────── */}
              <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                <h2 className="font-semibold text-gray-900">Opt-out Keywords</h2>
                <p className="text-sm text-gray-500">
                  Inbound messages matching any of these (case-insensitive) mark the contact opted out.
                </p>
                <div className="flex flex-wrap gap-2">
                  {(org.opt_out_keywords || []).map((kw) => (
                    <span
                      key={kw}
                      className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-mono px-2 py-1 rounded-full"
                    >
                      {kw}
                      <button
                        type="button"
                        aria-label={`Remove ${kw}`}
                        onClick={() => removeKeyword(kw)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {(!org.opt_out_keywords || org.opt_out_keywords.length === 0) && (
                    <span className="text-xs text-gray-400 italic">No keywords configured.</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add keyword (e.g. STOPALL)"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addKeyword();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={addKeyword}
                    className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
              </section>

              {/* ── Cost rates ──────────────────────────────────── */}
              <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                <h2 className="font-semibold text-gray-900">Cost Rates</h2>
                <p className="text-sm text-gray-500">
                  Override Twilio's defaults if you have negotiated rates. Only new messages
                  use the new rate; historical cost records are unchanged.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMS — per segment ($)</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={org.sms_cost_per_segment ?? ""}
                      onChange={(e) =>
                        setOrg({
                          ...org,
                          sms_cost_per_segment: e.target.value === "" ? null : parseFloat(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">MMS — per message ($)</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={org.mms_cost_per_message ?? ""}
                      onChange={(e) =>
                        setOrg({
                          ...org,
                          mms_cost_per_message: e.target.value === "" ? null : parseFloat(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
              </section>

              {/* ── Save bar ────────────────────────────────────── */}
              <div className="flex items-center gap-4">
                <button
                  onClick={saveOrgSettings}
                  disabled={orgSaveStatus === "saving"}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {orgSaveStatus === "saving" ? "Saving…" : "Save Settings"}
                </button>
                {orgSaveStatus === "saved" && (
                  <span className="text-sm text-green-600">Saved.</span>
                )}
                {orgSaveStatus === "error" && (
                  <span className="text-sm text-red-600">Save failed — check errors above.</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
